import { LoaderCircle } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useDrag } from 'react-dnd';
import { Text } from '@/ui/design-system/components';
import ErrorBoundary from '@/ui/containers/ErrorBoundary';
import InlineVisualError from '@/ui/components/AiAssistant/visuals/primitives/InlineVisualError';
import {
    canExcludeRows,
    selectVisualForToolResult,
} from '@/ui/components/AiAssistant/visuals';
import ReportVisualFrame from './ReportVisualFrame';

// The react-dnd item type for report widget reordering. ReportWidgetGrid is the
// single container-level drop target; this widget is the drag source.
export const REPORT_WIDGET_DND = 'ReportWidget';

const Centered = ({ children }) => (
    <div className="flex items-center justify-center p-4">{children}</div>
);

// One report widget: the report view owns the fetch (via useQueries) and passes
// this widget's config + query result down. It renders the same inline visual the
// assistant uses, but injects the report frame (gray tile + edit affordances) so
// the visual itself stays surface-agnostic. Loading / error / no-visual states
// render THROUGH the frame too, so a broken widget keeps its grip + Delete menu.
// The drag source lives here (preview + live index) while the grip — the source
// node — lives in the frame's edit header.
function ReportWidget({
    args,
    data,
    isLoading,
    isError,
    error,
    editing = false,
    id,
    index,
    title,
    onToggleAggregate,
    onExcludeRow,
    onEdit,
    onDuplicate,
    onRemove,
    onDropPersist,
}) {
    const cardRef = useRef(null);
    const [{ isDragging }, drag, preview] = useDrag({
        type: REPORT_WIDGET_DND,
        item: () => {
            // Capture the card's center at drag start; the grid drop target adds the
            // pointer delta to track the dragged card's live center (the mouse sits
            // at the far-left grip, so it can't be used directly).
            const r = cardRef.current?.getBoundingClientRect();
            return {
                id,
                index,
                startCenterX: r ? r.left + r.width / 2 : 0,
                startCenterY: r ? r.top + r.height / 2 : 0,
            };
        },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        end: () => onDropPersist?.(),
    });
    preview(cardRef);

    // The drag connector + action handlers change identity every render (react-dnd
    // recreates the connector; ReportView passes inline handlers), so read them from
    // a ref. That lets `Frame` stay referentially stable across renders — only
    // remounting when edit mode toggles — which is essential: a fresh Frame each
    // render would remount the grip mid-reorder and drop the drag.
    const latest = useRef();
    latest.current = { drag, onEdit, onDuplicate, onRemove };
    const Frame = useMemo(() => {
        const ReportFrame = ({ breadcrumb, children }) => {
            const cb = latest.current;
            return (
                <ReportVisualFrame
                    breadcrumb={breadcrumb}
                    editing={editing}
                    dragRef={editing ? cb.drag : undefined}
                    onEdit={() => cb.onEdit?.(id)}
                    onDuplicate={() => cb.onDuplicate?.(id)}
                    onRemove={() => cb.onRemove?.(id)}
                >
                    {children}
                </ReportVisualFrame>
            );
        };
        return ReportFrame;
    }, [editing, id]);

    // Loading / error states have no model to build a breadcrumb from; show what the
    // config already carries (geo · source) so the header isn't empty.
    const fallbackBreadcrumb = {
        geo: args?.geo,
        dataSource: args?.data_source,
        typeLabel: '',
    };

    let content;
    if (isLoading) {
        content = (
            <Frame breadcrumb={fallbackBreadcrumb}>
                <Centered>
                    <LoaderCircle
                        size={20}
                        className="animate-spin text-muted-foreground"
                    />
                </Centered>
            </Frame>
        );
    } else if (isError) {
        content = (
            <Frame breadcrumb={fallbackBreadcrumb}>
                <Centered>
                    <Text size="sm" color="muted">
                        {error?.message || 'Could not load this widget.'}
                    </Text>
                </Centered>
            </Frame>
        );
    } else {
        const visual = selectVisualForToolResult(args, data);
        if (!visual) {
            content = (
                <Frame breadcrumb={fallbackBreadcrumb}>
                    <Centered>
                        <Text size="sm" color="muted">
                            This widget can no longer be rendered.
                        </Text>
                    </Centered>
                </Frame>
            );
        } else {
            // Key the boundary on config + data so editing a widget that crashed the
            // visual (the builder throws on shape drift) remounts a fresh boundary
            // instead of staying latched on the fallback. The data half of the key is
            // its identifying content (metrics + entity), not the whole payload —
            // list-widget payloads carry per-entity time series too large to
            // stringify per render. The fallback renders inside the frame so a
            // crashed widget still has its grip + Delete menu.
            const Fallback = (props) => (
                <Frame breadcrumb={fallbackBreadcrumb}>
                    <Centered>
                        <InlineVisualError {...props} />
                    </Centered>
                </Frame>
            );
            content = (
                <ErrorBoundary
                    key={JSON.stringify([
                        args,
                        data?.result?.metrics,
                        data?._entity,
                    ])}
                    FallbackComponent={Fallback}
                >
                    <visual.Component
                        {...visual.props}
                        Frame={Frame}
                        dense
                        showEmptyState
                        title={title}
                        onToggleAggregate={onToggleAggregate}
                        onExcludeRow={
                            editing && canExcludeRows(args)
                                ? onExcludeRow
                                : undefined
                        }
                    />
                </ErrorBoundary>
            );
        }
    }

    return (
        <div
            ref={cardRef}
            className={`transition-opacity ${isDragging ? 'opacity-40' : ''}`}
        >
            {content}
        </div>
    );
}

export default ReportWidget;
